import { LoadingButton } from "@mui/lab";
import { Grid2 as Grid } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { HighlightSelectBox, SectionPaper } from "../../../components";
import { SelectBoxOption } from "../../../interfaces";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { useCreateUserPreferenceMutation, useLazyGetCurrencyQuery } from "../../../redux/services";
import { closeConfirmationDialog, openConfirmationDialog } from "../../../redux/slices/confirmationDialog";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { HasAccess } from "../../../router/authorization";
import { PERMISSIONS } from "../../../router/permission";
import { USD } from "../../viewPricing/exchangeRatesSelection";
import { CURRENCY_KEY } from "../utils";
import { getUserSelectedCurrency } from "../../../utils";

export const BasicDetails: React.FC<{ refetch: any }> = ({ refetch }) => {

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { userDetails, userPreferences } = useAppSelector(store => store.auth);
    const { perm } = userDetails;

    const [getCurrencies, currencyRes] = useLazyGetCurrencyQuery();
    const [saveCurrency, response] = useCreateUserPreferenceMutation();

    const [currency, setCurrency] = React.useState<SelectBoxOption>(USD);

    const creatable = React.useMemo(() => HasAccess(perm, PERMISSIONS.PROPOSAL_CREATE), [perm]);
    const updatable = React.useMemo(() => HasAccess(perm, PERMISSIONS.PROPOSAL_UPDATE), [perm]);
    const isViewOnly = !(updatable && creatable);

    const onSaveBtnClicked = () => {
        dispatch(openConfirmationDialog({
            title: "Update Operation:",
            body: "Are you sure want to update?",
            positiveBtn: "Yes",
            negativeBtn: "No",
            onOk: onSaveCurrency,
            onNegativeBtn: () => dispatch(closeConfirmationDialog())
        }));
    };

    const onSaveCurrency = async () => {
        dispatch(closeConfirmationDialog());
        dispatch(openBackdrop("Saving the selected currency proposal..."))
        await saveCurrency({ key: CURRENCY_KEY, value: currency?.value }).unwrap().then((res: any) => {
            if (!res?.title) {
                enqueueSnackbar(res?.message ?? 'Currency Saved Successfully!', { variant: "success" });
                dispatch(closeBackdrop());
                refetch();
            }
        }).catch((error: any) => {
            console.error(`Error while saving the currency: ${error}`)
            enqueueSnackbar(error?.data?.title ?? 'Oops! Something went wrong unable to save the currency.', { variant: "error" })
        }).finally(() => {
            dispatch(closeBackdrop());
        });

    };

    const isLoading = response.isLoading || currencyRes.isFetching || currencyRes.isLoading;

    const giveMeButtonName = () => {
        if (response.isError) {
            return `Retry Save`
        }

        return `Save`;
    };

    React.useEffect(() => {
        getCurrencies({});
        // eslint-disable-next-line
    }, []);
    
    React.useEffect(() => {
        if (userPreferences.length > 0) {
            const selectedCurrencyDetails = getUserSelectedCurrency(userPreferences);
            setCurrency(selectedCurrencyDetails?.value ? currencyRes?.data?.find((c: SelectBoxOption) => c.value === selectedCurrencyDetails?.value) ?? USD : USD);
        }
        // eslint-disable-next-line
    }, [JSON.stringify(userPreferences), currencyRes.isSuccess]);

    return (
        <SectionPaper
            stack
            title={"Choose the default currency"}
            renderButtons={
                isViewOnly
                    ? [] :
                    [
                        <LoadingButton id="u-c-btn" loading={isLoading} variant="contained" sx={{ width: 200 }} color="primary" onClick={() => onSaveBtnClicked()}>
                            {giveMeButtonName()}
                        </LoadingButton>
                    ]
            }
        >
            <Grid container>
                <Grid size={{ xs: 12, md: 12, lg: 6 }}>
                    <HighlightSelectBox
                        disableClearable
                        size="small"
                        label={"Default Currency"}
                        margin={"none"}
                        loading={currencyRes.isLoading}
                        value={currency}
                        options={
                            currencyRes?.data || []
                        }
                        onChange={(data: SelectBoxOption) => {
                            setCurrency(data);
                        }}
                    />
                </Grid>
            </Grid>
        </SectionPaper>
    );
}